import { useTranslation } from 'react-i18next';
import styles from './PerformanceTable.module.scss';
import { useMemo, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import dropdownArrowLight from '../../../assets/images/LightTheme/down_arrow_light.svg';
import dropdownArrowDark from '../../../assets/images/DarkTheme/down_arrow_dark.svg';
import Tooltip from '../../common/Tooltip/Tooltip';
import MyAccountsLoader from '../../common/Loaders/MyAccountsLoader/MyAccountsLoader';
import ErrorInPageScreen from '../../common/ErrorInPageScreen/ErrorInPageScreen';
import {
  HATVABOStatDetailsValueItem,
  HATVAboStatisticDetails,
  useGetHATVAboStatisticDetailsQuery,
} from '../../../redux/services/hatvAboStatisticAPI';
import { useABOProfileNavigation, isSpecialQType, truncateValue } from '../../../utils/common';
import { FormatLocalizedNumber } from '../../../utils/NumberFormatter';
export interface PerformanceTableData {
  performanceBonus: string | undefined;
  qType: string | undefined;
  qvUsed:
    | {
        aboNumber: string;
        affiliateCode: string;
        localName: string;
        name: string;
      }
    | undefined;
  qLegs: string | undefined;
  groupSize: string | undefined;
  contributorCount: string | undefined;
  activeCustomerCount: string | undefined;
  ppvTopUpPv: string | undefined;
  ppvVcs: string | undefined;
  ppvVcsPercent: string | undefined;
  ppvCustomerPv: string | undefined;
  ppvCustomerPvPercent: string | undefined;
  gpv: string | undefined;
  rubyPv: string | undefined;
}

type RowConfig = { key: string; children?: RowConfig[] };

type TableDataKey = keyof Omit<PerformanceTableData, 'qvUsed'>;

const PerformanceTable = ({
  closingMonth,
  affiliateCode,
  aboNumber,
}: {
  closingMonth: string;
  affiliateCode: string;
  aboNumber: number;
}) => {
  const { t } = useTranslation();
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const handleABOInfoClick = useABOProfileNavigation();
  const months = [
    'Sept',
    'Oct',
    'Nov',
    'Dec',
    'Jan',
    'Feb',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug',
  ];

  const rowConfig: Array<RowConfig> = [
    { key: 'performanceBonus' },
    { key: 'qType' },
    { key: 'qLegs' },
    { key: 'groupSize' },
    { key: 'contributorCount' },
    { key: 'activeCustomerCount' },
    {
      key: 'ppv',
      children: [
        { key: 'ppvTopUpPv' },
        { key: 'ppvVcs' },
        { key: 'ppvVcsPercent' },
        { key: 'ppvCustomerPv' },
        { key: 'ppvCustomerPvPercent' },
      ],
    },
    { key: 'gpv' },
    { key: 'rubyPv' },
  ];

  const {
    data: performanceRes,
    isFetching,
    isError,
    refetch,
  } = useGetHATVAboStatisticDetailsQuery({
    closingMonth,
    affiliateCode,
    aboNumber,
  });

  const data = useMemo(() => {
    if (!performanceRes?.body) return [];
    return mapABOStatisticsToPerformanceTableData(performanceRes.body, userAffiliateCode);
  }, [performanceRes]);

  if (isFetching) {
    return <MyAccountsLoader num={4} hideCustomViewPanel={true} />;
  }

  if (isError) {
    return (
      <ErrorInPageScreen
        handleClickTryAgain={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className={`performance_table_wrapper ${styles.table_wrapper}`}>
      <table className={`tbl ${styles.table}`}>
        <thead className={styles.tbl_head_wrapper}>
          <tr className={`${styles.tbl_head}`}>
            <th></th>
            {months.map((month) => (
              <th key={month}>{t(`hatv_months.${month}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody className={`${styles.tbl_body}`}>
          {rowConfig.map((conf, i) => {
            const rowClass = i % 2 === 0 ? styles.even_row : styles.odd_row;

            if (conf.children) {
              return (
                <RowGroup
                  conf={conf}
                  key={conf.key}
                  months={months}
                  data={data}
                  rowClass={rowClass}
                  userAffiliateCode={userAffiliateCode}
                />
              );
            }

            return (
              <tr key={conf.key} className={`tbl_row ${rowClass} ${styles.tbl_row}`}>
                <td>{t(`performance_table.labels.${conf.key}`)}</td>

                {months.map((month, i) => {
                  let val = data[i]?.[conf.key as TableDataKey] ?? '-';
                  if (val !== '-' && conf.key !== 'qType' && !String(val).includes('%')) {
                    val = FormatLocalizedNumber(userAffiliateCode, val);
                  }
                  const qvUsed = data[i]?.qvUsed;
                  const showQTypeTooltip =
                    conf.key === 'qType' && qvUsed && val !== '-' && isSpecialQType(val);
                  if (showQTypeTooltip) {
                    const { aboNumber, name } = qvUsed;
                    return (
                      <td key={`${conf.key}-${month}`}>
                        <div className={`leg_tooltip ${styles.leg_tooltip}`}>
                          <Tooltip
                            content={
                              <div className={styles.tooltip_content_wrapper}>
                                <div className={styles.flagged_qualifier_wrapepr}>
                                  <span className={styles.flagged_qualifier_title}>{val}:</span>
                                  <div className={styles.abo_qualifier_details}>
                                    <span
                                      className={styles.abo_qualifier_name}
                                      onClick={() => handleABOInfoClick(aboNumber, affiliateCode)}
                                    >
                                      {name}
                                    </span>
                                    <span className={styles.abo_qualifier_no}>#{aboNumber}</span>
                                  </div>
                                </div>
                              </div>
                            }
                            width="257"
                            height="auto"
                            left="0%"
                            right="0%"
                            bottom="33px"
                            tipPos={true}
                          >
                            <span className={styles.tooltip_value}>{val}</span>
                          </Tooltip>
                        </div>
                      </td>
                    );
                  }

                  return <td key={`${conf.key}-${month}`}>{val}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RowGroup = ({
  conf,
  months,
  data,
  rowClass,
  userAffiliateCode,
}: {
  conf: RowConfig;
  months: string[];
  data: PerformanceTableData[];
  rowClass: string;
  userAffiliateCode: string;
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow = currentTheme === 'light' ? dropdownArrowLight : dropdownArrowDark;
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };
  return (
    <>
      <tr className={`tbl_row ${rowClass} ${styles.tbl_row}`}>
        <td>
          <span className={styles.group_head} onClick={toggleExpanded}>
            <img
              src={dropdownArrow}
              alt="Downkey"
              className={`${styles.arrow_icon} ${expanded ? styles.rotate : ''}`}
            />
            {t(`performance_table.labels.${conf.key}`)}
          </span>
        </td>
        {months.map((month, i) => (
          <td key={`${conf.key}-${month}`}>{data[i]?.[conf.key as TableDataKey] ?? '-'}</td>
        ))}
      </tr>
      {expanded &&
        conf.children?.map((child) => {
          return (
            <tr
              className={`tbl_row tbl_grp_row ${rowClass} ${styles.tbl_row} ${styles.tbl_grp_row}`}
            >
              <td>{t(`performance_table.labels.${child.key}`)}</td>
              {months.map((month, i) => {
                let val = data[i]?.[child.key as TableDataKey] ?? '-';

                if (val !== '-' && !String(val).includes('%')) {
                  val = FormatLocalizedNumber(userAffiliateCode, val);
                }
                return <td key={`${conf.key}-${month}`}>{val}</td>;
              })}
            </tr>
          );
        })}
    </>
  );
};

const MONTHS_SEQUENCE = [
  '09', // Sept
  '10', // Oct
  '11', // Nov
  '12', // Dec
  '01', // Jan
  '02', // Feb
  '03', // March
  '04', // April
  '05', // May
  '06', // June
  '07', // July
  '08', // Aug
];

const mapABOStatisticsToPerformanceTableData = (
  res: HATVAboStatisticDetails,
  userAffiliateCode: string
): PerformanceTableData[] => {
  const findValue = <T extends HATVABOStatDetailsValueItem<number | string>>(
    monthCode: string,
    items: T[] = []
  ): string | undefined => {
    if (!Array.isArray(items) || !monthCode) return undefined;
    const item = items.find((entry) => entry.closingMonth?.substring(4, 6) === monthCode);
    if (item?.value == null) return undefined;
    return truncateValue(item.value);
  };

  return MONTHS_SEQUENCE.map<PerformanceTableData>((monthCode) => {
    const performanceBonus = findValue(
      monthCode,
      res?.leaderStatisticDetail?.performanceBonusPercentage
    );

    const ppvVcsPercent = findValue(
      monthCode,
      res?.leaderStatisticDetail?.personalPvDetail?.vcsPercentage
    );

    const ppvCustomerPvPercent = findValue(
      monthCode,
      res?.leaderStatisticDetail?.personalPvDetail?.customerPvPercentage
    );

    const qType = res?.leaderStatisticDetail?.qType?.find(
      (item) => item.closingMonth.substring(4, 6) === monthCode
    );

    const ppv = findValue(monthCode, res?.leaderStatisticDetail?.personalPvDetail?.personalPv);

    return {
      performanceBonus: performanceBonus ? performanceBonus + '%' : undefined,
      qType: qType?.value,
      qvUsed: qType?.qvUsed,
      qLegs: findValue(monthCode, res?.leaderStatisticDetail?.qLeg),
      groupSize: findValue(monthCode, res?.leaderStatisticDetail?.groupSize),
      contributorCount: findValue(monthCode, res?.leaderStatisticDetail?.contributorCount),
      activeCustomerCount: findValue(monthCode, res?.leaderStatisticDetail?.activeCustomerCount),
      ppv: ppv ? FormatLocalizedNumber(userAffiliateCode, ppv) : undefined,
      ppvTopUpPv: findValue(monthCode, res?.leaderStatisticDetail?.personalPvDetail?.topUpPv),
      ppvVcs: findValue(monthCode, res?.leaderStatisticDetail?.personalPvDetail?.vcs),
      ppvVcsPercent: ppvVcsPercent ? ppvVcsPercent + '%' : undefined,
      ppvCustomerPv: findValue(monthCode, res?.leaderStatisticDetail?.personalPvDetail?.customerPv),
      ppvCustomerPvPercent: ppvCustomerPvPercent ? ppvCustomerPvPercent + '%' : undefined,
      gpv: findValue(monthCode, res?.leaderStatisticDetail?.groupPv),
      rubyPv: findValue(monthCode, res?.leaderStatisticDetail?.rubyPv),
    };
  });
};

export default PerformanceTable;
